import type { Locale } from "@/lib/i18n";
import {
  BrainCircuit,
  Building2,
  ClipboardList,
  Cloud,
  Code,
  GraduationCap,
  Handshake,
  Lock,
  Network,
  ShieldCheck,
  Smartphone,
  Stethoscope,
  Users,
  type LucideIcon,
} from "lucide-react";

/**
 * Information architecture for the navbar mega menus (desktop panels + mobile
 * accordion). Healthcare is the lead menu — it merges the old "Solutions" and
 * "Health Tech" dropdowns; non-healthcare solutions live under Services.
 *
 * Labels are short nav labels (not page titles), so they're kept here with all
 * three languages rather than derived from each page's data file.
 */

type Localized = { en: string; de: string; fr: string };

export interface NavLink {
  href: string;
  label: Localized;
}

export interface NavGroup {
  heading: Localized;
  /** Small icon shown before the group heading. */
  icon: LucideIcon;
  links: NavLink[];
}

/** One visual column of a mega menu; may stack several groups. */
export type NavColumn = NavGroup[];

export function navText(text: Localized, locale: Locale): string {
  if (locale === "de-de") return text.de;
  if (locale === "fr-fr") return text.fr;
  return text.en;
}

export const healthcareMenu: NavColumn[] = [
  [
    {
      heading: { en: "By specialty", de: "Nach Fachrichtung", fr: "Par spécialité" },
      icon: Stethoscope,
      links: [
        {
          href: "/health-tech/primary-urgent-care-emr",
          label: { en: "Primary & urgent care", de: "Primär- & Akutversorgung", fr: "Soins primaires & non programmés" },
        },
        {
          href: "/health-tech/rehab-therapy-emr",
          label: { en: "Rehab & therapy", de: "Reha & Therapie", fr: "Rééducation & thérapies" },
        },
        {
          href: "/mental-health-emr",
          label: { en: "Mental health", de: "Psychische Gesundheit", fr: "Santé mentale" },
        },
        {
          href: "/health-tech/dental-practice-management-software",
          label: { en: "Dental", de: "Zahnmedizin", fr: "Dentaire" },
        },
        {
          href: "/health-tech/womens-health-fertility-emr",
          label: { en: "Women's health & fertility", de: "Frauengesundheit & Kinderwunsch", fr: "Santé de la femme & fertilité" },
        },
        {
          href: "/health-tech/eye-care-emr",
          label: { en: "Eye care", de: "Augenheilkunde & Optometrie", fr: "Ophtalmologie & optométrie" },
        },
        {
          href: "/health-tech/surgery-asc-software",
          label: { en: "Surgery & ASCs", de: "Chirurgie & ambulante OP-Zentren", fr: "Chirurgie & centres ambulatoires" },
        },
        {
          href: "/health-tech/oncology-infusion-emr",
          label: { en: "Oncology & infusion", de: "Onkologie & Infusion", fr: "Oncologie & perfusion" },
        },
        {
          href: "/health-tech/cardiology-diagnostic-emr",
          label: { en: "Cardiology & diagnostics", de: "Kardiologie & Diagnostik", fr: "Cardiologie & diagnostic" },
        },
        {
          href: "/health-tech/home-health-hospice-software",
          label: { en: "Home health & hospice", de: "Häusliche Pflege & Hospiz", fr: "Soins à domicile & palliatifs" },
        },
        {
          href: "/aesthetic-emr-ehr-development",
          label: { en: "Aesthetic & medspa", de: "Ästhetik & Medspa", fr: "Esthétique & médispa" },
        },
        {
          href: "/health-tech/specialties",
          label: { en: "All specialties", de: "Alle Fachrichtungen", fr: "Toutes les spécialités" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "Clinical platforms", de: "Klinische Plattformen", fr: "Plateformes cliniques" },
      icon: ClipboardList,
      links: [
        {
          href: "/custom-emr-ehr-software-development",
          label: { en: "Custom EMR & EHR", de: "Individuelle EMR & EHR", fr: "EMR & EHR sur mesure" },
        },
        {
          href: "/health-tech/saas-emr-product",
          label: { en: "SaaS EMR products", de: "SaaS-EMR-Produkte", fr: "Produits EMR SaaS" },
        },
        {
          href: "/health-tech/charting-imaging-software",
          label: { en: "Charting & imaging", de: "Charting & Bildgebung", fr: "Dossier clinique & imagerie" },
        },
        {
          href: "/health-tech/procedure-planning-software",
          label: { en: "Procedure planning", de: "Behandlungsplanung", fr: "Planification des traitements" },
        },
      ],
    },
    {
      heading: { en: "Patient & practice", de: "Patienten & Praxis", fr: "Patients & cabinet" },
      icon: Users,
      links: [
        {
          href: "/health-tech/practice-management-software",
          label: { en: "Practice management", de: "Praxismanagement", fr: "Gestion de cabinet" },
        },
        {
          href: "/health-tech/appointment-scheduling-software",
          label: { en: "Appointment scheduling", de: "Terminplanung", fr: "Prise de rendez-vous" },
        },
        {
          href: "/health-tech/patient-portal-software",
          label: { en: "Patient portals", de: "Patientenportale", fr: "Portails patients" },
        },
        {
          href: "/health-tech/intake-forms-software",
          label: { en: "Patient intake forms", de: "Digitale Patientenaufnahme", fr: "Formulaires d'admission" },
        },
        {
          href: "/health-tech/telemedicine-software",
          label: { en: "Telemedicine", de: "Telemedizin", fr: "Télémédecine" },
        },
        {
          href: "/health-tech/mhealth-app-development",
          label: { en: "mHealth apps", de: "mHealth-Apps", fr: "Applications mHealth" },
        },
        {
          href: "/health-tech/medical-billing-rcm",
          label: { en: "Medical billing & RCM", de: "Medizinische Abrechnung & RCM", fr: "Facturation médicale & RCM" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "Data & interoperability", de: "Daten & Interoperabilität", fr: "Données & interopérabilité" },
      icon: Network,
      links: [
        {
          href: "/health-tech/fhir-integration",
          label: { en: "FHIR interoperability", de: "FHIR-Interoperabilität", fr: "Interopérabilité FHIR" },
        },
        {
          href: "/health-tech/mirth-connect-integration",
          label: { en: "Mirth Connect integration", de: "Mirth-Connect-Integration", fr: "Intégration Mirth Connect" },
        },
        {
          href: "/health-tech/healthcare-data-migration",
          label: { en: "Healthcare data migration", de: "Migration von Gesundheitsdaten", fr: "Migration de données de santé" },
        },
        {
          href: "/health-tech/healthcare-data-analytics-platform",
          label: { en: "Healthcare analytics", de: "Gesundheitsdatenanalyse", fr: "Analytique de santé" },
        },
        {
          href: "/health-tech/healthcare-automation-services",
          label: { en: "Healthcare automation", de: "Automatisierung im Gesundheitswesen", fr: "Automatisation en santé" },
        },
      ],
    },
    {
      heading: { en: "Compliance", de: "Compliance", fr: "Conformité" },
      icon: ShieldCheck,
      links: [
        {
          href: "/health-tech/hipaa-compliant-software",
          label: { en: "HIPAA-compliant software", de: "HIPAA-konforme Software", fr: "Logiciels conformes HIPAA" },
        },
        {
          href: "/trust-center",
          label: { en: "Trust Center", de: "Trust Center", fr: "Trust Center" },
        },
      ],
    },
  ],
];

export const companyMenu: NavColumn[] = [
  [
    {
      heading: { en: "Who we are", de: "Wer wir sind", fr: "Qui sommes-nous" },
      icon: Building2,
      links: [
        {
          href: "/team",
          label: { en: "Our team", de: "Unser Team", fr: "Notre équipe" },
        },
        {
          href: "/collaborations",
          label: { en: "Partnerships", de: "Partnerschaften", fr: "Partenariats" },
        },
        {
          href: "/about",
          label: { en: "About Woltrio", de: "Über Woltrio", fr: "À propos de Woltrio" },
        },
        {
          href: "/reviews",
          label: { en: "Client reviews", de: "Kundenbewertungen", fr: "Avis clients" },
        },
        {
          href: "/how-we-work",
          label: { en: "How we work", de: "So arbeiten wir", fr: "Notre méthode" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "Trust & security", de: "Vertrauen & Sicherheit", fr: "Confiance & sécurité" },
      icon: Lock,
      links: [
        {
          href: "/trust-center",
          label: { en: "Trust Center", de: "Trust Center", fr: "Trust Center" },
        },
        {
          href: "/privacy-policy",
          label: { en: "Privacy policy", de: "Datenschutzerklärung", fr: "Politique de confidentialité" },
        },
        {
          href: "/terms-of-use",
          label: { en: "Terms of use", de: "Nutzungsbedingungen", fr: "Conditions d'utilisation" },
        },
        {
          href: "/protect-yourself-from-fraud",
          label: { en: "Fraud protection", de: "Betrugsschutz", fr: "Protection contre la fraude" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "Work with us", de: "Arbeiten Sie mit uns", fr: "Travailler avec nous" },
      icon: Handshake,
      links: [
        {
          href: "/hire",
          label: { en: "Hire dedicated developers", de: "Dedizierte Entwickler einstellen", fr: "Recruter des développeurs dédiés" },
        },
        {
          href: "/request-client-access",
          label: { en: "Client portal", de: "Kundenportal", fr: "Portail client" },
        },
        {
          href: "/contact",
          label: { en: "Contact", de: "Kontakt", fr: "Contact" },
        },
      ],
    },
  ],
];

export const servicesMenu: NavColumn[] = [
  [
    {
      heading: { en: "Product engineering", de: "Produktentwicklung", fr: "Ingénierie produit" },
      icon: Code,
      links: [
        {
          href: "/mvp-development",
          label: { en: "MVP development", de: "MVP-Entwicklung", fr: "Développement de MVP" },
        },
        {
          href: "/website-development",
          label: { en: "Website development", de: "Website-Entwicklung", fr: "Développement de sites web" },
        },
        {
          href: "/services/frontend",
          label: { en: "Front-end development", de: "Frontend-Entwicklung", fr: "Développement front-end" },
        },
        {
          href: "/services/backend",
          label: { en: "Backend development", de: "Backend-Entwicklung", fr: "Développement back-end" },
        },
        {
          href: "/services/api",
          label: { en: "APIs & integration", de: "APIs & Integration", fr: "API & intégration" },
        },
        {
          href: "/services/legacy-software-modernization",
          label: { en: "Legacy modernization", de: "Legacy-Modernisierung", fr: "Modernisation legacy" },
        },
        {
          href: "/services/ui-ux-design",
          label: { en: "UI/UX design", de: "UI/UX-Design", fr: "Design UI/UX" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "Mobile apps", de: "Mobile Apps", fr: "Applications mobiles" },
      icon: Smartphone,
      links: [
        {
          href: "/services/mobile-app-development-services",
          label: { en: "Mobile app development", de: "Mobile-App-Entwicklung", fr: "Développement d'applications mobiles" },
        },
        {
          href: "/services/ios-app-development",
          label: { en: "iOS development", de: "iOS-Entwicklung", fr: "Développement iOS" },
        },
        {
          href: "/services/android-app-development",
          label: { en: "Android development", de: "Android-Entwicklung", fr: "Développement Android" },
        },
        {
          href: "/services/cross-platform-app-development",
          label: { en: "Cross-platform apps", de: "Cross-Platform-Apps", fr: "Applications multiplateformes" },
        },
        {
          href: "/health-tech/mhealth-app-development",
          label: { en: "mHealth apps", de: "mHealth-Apps", fr: "Applications mHealth" },
        },
        {
          href: "/health-tech/telemedicine-software",
          label: { en: "Telemedicine apps", de: "Telemedizin-Apps", fr: "Applications de télémédecine" },
        },
      ],
    },
    {
      heading: { en: "Beyond healthcare", de: "Über das Gesundheitswesen hinaus", fr: "Au-delà de la santé" },
      icon: GraduationCap,
      links: [
        {
          href: "/lms-development",
          label: { en: "LMS development", de: "LMS-Entwicklung", fr: "Développement LMS" },
        },
        {
          // /edutech/* is retired and redirects here (next.config.ts).
          href: "/services/frontend/edutech",
          label: { en: "EdTech", de: "EdTech", fr: "EdTech" },
        },
      ],
    },
  ],
  [
    {
      heading: { en: "AI & data", de: "KI & Daten", fr: "IA & données" },
      icon: BrainCircuit,
      links: [
        {
          href: "/services/ai",
          label: { en: "AI development", de: "KI-Entwicklung", fr: "Développement IA" },
        },
        {
          href: "/services/ml",
          label: { en: "Machine learning", de: "Machine Learning", fr: "Machine learning" },
        },
        {
          href: "/ai-automation-solutions-services",
          label: { en: "AI automation", de: "KI-Automatisierung", fr: "Automatisation par l'IA" },
        },
        {
          href: "/ai-chatbot-solutions-development",
          label: { en: "AI chatbots", de: "KI-Chatbots", fr: "Chatbots IA" },
        },
        {
          href: "/services/automation",
          label: { en: "Workflow automation", de: "Workflow-Automatisierung", fr: "Automatisation des workflows" },
        },
        {
          href: "/services/analytics",
          label: { en: "Data analytics", de: "Datenanalyse", fr: "Analyse de données" },
        },
      ],
    },
    {
      heading: { en: "Cloud & DevOps", de: "Cloud & DevOps", fr: "Cloud & DevOps" },
      icon: Cloud,
      links: [
        {
          href: "/services/cloud",
          label: { en: "Cloud applications", de: "Cloud-Anwendungen", fr: "Applications cloud" },
        },
        {
          href: "/services/cloud-devops-engineering",
          label: { en: "Cloud & DevOps engineering", de: "Cloud- & DevOps-Engineering", fr: "Ingénierie cloud & DevOps" },
        },
        {
          href: "/services/devops",
          label: { en: "DevOps", de: "DevOps", fr: "DevOps" },
        },
      ],
    },
  ],
];
